import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '../../lib/utils';
import type { TovIconName } from '../../contracts/iconography';
import { TovIcon } from '../TovIcon/TovIcon';

const buttonVariants = cva(
  'tov:inline-flex tov:shrink-0 tov:items-center tov:justify-center tov:gap-2 tov:rounded-md tov:text-sm tov:font-medium tov:whitespace-nowrap tov:transition-all tov:outline-none tov:focus-visible:border-ring tov:focus-visible:ring-[3px] tov:focus-visible:ring-ring/50 tov:disabled:pointer-events-none tov:disabled:opacity-50 tov:aria-invalid:border-destructive tov:aria-invalid:ring-destructive/20 tov:dark:aria-invalid:ring-destructive/40 tov:[&_svg]:pointer-events-none tov:[&_svg]:shrink-0 tov:[&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        default:
          'tov:bg-primary tov:text-primary-foreground tov:hover:bg-primary/90',
        destructive:
          'tov:bg-destructive tov:text-white tov:hover:bg-destructive/90 tov:focus-visible:ring-destructive/20 tov:dark:bg-destructive/60 tov:dark:focus-visible:ring-destructive/40',
        outline:
          'tov:border tov:bg-background tov:shadow-xs tov:hover:bg-accent tov:hover:text-accent-foreground tov:dark:border-input tov:dark:bg-input/30 tov:dark:hover:bg-input/50',
        secondary:
          'tov:bg-secondary tov:text-secondary-foreground tov:hover:bg-secondary/80',
        ghost:
          'tov:hover:bg-accent tov:hover:text-accent-foreground tov:dark:hover:bg-accent/50',
        link: 'tov:text-primary tov:underline-offset-4 tov:hover:underline',
      },
      size: {
        default: 'tov:h-9 tov:px-4 tov:py-2 tov:has-[>svg]:px-3',
        xs: 'tov:h-6 tov:gap-1 tov:rounded-md tov:px-2 tov:text-xs tov:has-[>svg]:px-1.5 tov:[&_svg:not([class*=size-])]:size-3',
        sm: 'tov:h-8 tov:gap-1.5 tov:rounded-md tov:px-3 tov:has-[>svg]:px-2.5',
        lg: 'tov:h-10 tov:rounded-md tov:px-6 tov:has-[>svg]:px-4',
        icon: 'tov:size-9',
        'icon-xs':
          'tov:size-6 tov:rounded-md tov:[&_svg:not([class*=size-])]:size-3',
        'icon-sm': 'tov:size-8',
        'icon-lg': 'tov:size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type TovButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function TovButton({
  className,
  type,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: TovButtonProps) {
  const Comp = asChild ? Slot : 'button';
  const buttonTypeProps = asChild ? {} : { type: type ?? 'button' };

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(
        buttonVariants({ variant, size, className }),
        'tov-button',
        variant === 'default' && 'tov-button--primary',
        variant === 'secondary' && 'tov-button--secondary',
        variant === 'ghost' && 'tov-button--ghost',
        variant === 'link' && 'tov-button--link',
      )}
      {...buttonTypeProps}
      {...props}
    />
  );
}

export interface TovIconButtonProps extends TovButtonProps {
  icon: TovIconName;
  directionalIcon?: boolean;
  size?: Extract<
    TovButtonProps['size'],
    'icon' | 'icon-xs' | 'icon-sm' | 'icon-lg'
  >;
}

function TovIconButton({
  asChild = false,
  children,
  className,
  directionalIcon = false,
  icon,
  size = 'icon',
  variant = 'ghost',
  ...props
}: TovIconButtonProps) {
  if (
    asChild &&
    React.isValidElement<{ children?: React.ReactNode }>(children)
  ) {
    return (
      <TovButton
        asChild
        className={cn('tov-icon-button', className)}
        size={size}
        variant={variant}
        {...props}
      >
        {React.cloneElement(children, {
          children: children.props.children ?? (
            <TovIcon directional={directionalIcon} name={icon} />
          ),
        })}
      </TovButton>
    );
  }

  return (
    <TovButton
      className={cn('tov-icon-button', className)}
      size={size}
      variant={variant}
      {...props}
    >
      <TovIcon directional={directionalIcon} name={icon} />
    </TovButton>
  );
}

export { TovButton, TovIconButton, buttonVariants };
